/**
 * StudioDome Notification Manager
 * A client-side JavaScript API for displaying notifications
 *
 * @example
 * // Simple notification
 * StudioDome.notify('Operation completed successfully');
 *
 * // With options
 * StudioDome.notify('New message received', {
 *   type: 'info',
 *   duration: 5000,
 *   icon: 'fa-envelope',
 *   actions: [{
 *     text: 'View',
 *     onClick: () => console.log('View clicked')
 *   }]
 * });
 */

(function(window) {
  'use strict';

  // Create namespace
  window.StudioDome = window.StudioDome || {};

  // Notification defaults
  const DEFAULTS = {
    type: 'info', // info, success, warning, error, primary, secondary
    duration: 5000, // ms, 0 for persistent
    position: 'top-right', // top-right, top-center, bottom-right, bottom-center, inline
    icon: null, // Font Awesome icon class
    closable: true,
    clickToDismiss: true,
    showProgress: true,
    animate: true,
    actions: [], // Array of {text, onClick, class}
    container: null, // For inline notifications
    onShow: null,
    onHide: null,
  };

  // Icon mappings
  const DEFAULT_ICONS = {
    info: 'fa-info-circle',
    success: 'fa-check-circle',
    warning: 'fa-exclamation-triangle',
    error: 'fa-times-circle',
    danger: 'fa-times-circle',
    primary: 'fa-bell',
    secondary: 'fa-comment',
  };

  // Active notifications
  const activeNotifications = new Map();
  let notificationIdCounter = 0;

  // Container references
  const containers = {};

  /**
   * Get or create notification container
   */
  function getContainer (position) {
    // Check if container already exists for this position
    if (containers[position]) {
      return containers[position];
    }

    // Check if there's an existing container in the DOM that we should clean up
    const existingContainers = document.querySelectorAll('.notification-container');
    existingContainers.forEach(container => {
      // Remove containers that aren't tracked in our containers object
      let isTracked = false;
      for (const key in containers) {
        if (containers[key] === container) {
          isTracked = true;
          break;
        }
      }
      if (!isTracked) {
        container.remove();
      }
    });

    const container = document.createElement('div');

    // Check if we're in a light theme environment
    const isLightTheme = document.body.classList.contains('theme-light') ||
      document.documentElement.classList.contains('theme-light') ||
      document.querySelector('.theme-light');

    // Add appropriate theme class to notification container
    container.className = 'notification-container' + (isLightTheme ? ' theme-light' : ' theme-dark');

    // Set inline styles immediately to ensure positioning works even before CSS loads
    // Force fixed positioning to escape any parent stacking contexts
    container.style.position = 'fixed';
    container.style.top = '20px';
    container.style.right = '20px';
    container.style.bottom = 'auto';
    container.style.left = 'auto';
    container.style.width = 'auto';
    container.style.height = 'auto';
    container.style.maxHeight = 'calc(100vh - 40px)';
    container.style.zIndex = '99999';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.gap = '10px';
    container.style.maxWidth = '400px';
    container.style.pointerEvents = 'none';
    container.style.overflow = 'visible';
    container.style.transform = 'none';
    container.style.filter = 'none';
    container.style.willChange = 'auto';

    // Apply position classes and adjust inline styles
    if (position.includes('bottom')) {
      container.classList.add('bottom');
      container.style.top = 'auto';
      container.style.bottom = '20px';
    }
    if (position.includes('center')) {
      container.classList.add('center');
      container.style.left = '50%';
      container.style.right = 'auto';
      container.style.transform = 'translateX(-50%)';
    }

    // Check if body has a transform that creates a stacking context
    const bodyComputedStyle = window.getComputedStyle(document.body);
    const hasBodyTransform = bodyComputedStyle.transform !== 'none' || 
                           bodyComputedStyle.filter !== 'none' ||
                           bodyComputedStyle.perspective !== 'none' ||
                           bodyComputedStyle.willChange === 'transform' ||
                           bodyComputedStyle.willChange === 'filter';

    if (hasBodyTransform) {
      // If body has transform, we need to compensate by using viewport units
      // and recalculating position on scroll/resize
      const updatePosition = () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        
        if (!position.includes('bottom')) {
          container.style.top = `${20 + scrollTop}px`;
        } else {
          container.style.bottom = `${20 - scrollTop}px`;
        }
        
        if (!position.includes('center')) {
          container.style.right = `${20 - scrollLeft}px`;
        }
      };

      // Update position immediately and on scroll/resize
      updatePosition();
      window.addEventListener('scroll', updatePosition, { passive: true });
      window.addEventListener('resize', updatePosition, { passive: true });
      
      // Store the event listeners so we can remove them later
      container._updatePosition = updatePosition;
    }

    // Append to body
    document.body.appendChild(container);

    containers[position] = container;

    return container;
  }

  /**
   * Create notification element
   */
  function createNotificationElement (message, options) {
    const id = `notification-${++notificationIdCounter}`;
    const notification = document.createElement('div');

    // Build classes - use standard callout structure
    const classes = ['callout', options.type];
    if (options.animate) {
      classes.push('notification-enter', 'animated');
    }
    if (options.clickToDismiss) {
      classes.push('clickable');
    }
    if (options.icon || DEFAULT_ICONS[options.type]) {
      classes.push('has-icon');
    }
    if (options.position === 'inline') {
      classes.push('inline');
    }

    notification.className = classes.join(' ');
    notification.id = id;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');

    // Build content
    let html = '';

    // Icon
    const iconClass = options.icon || DEFAULT_ICONS[options.type];
    if (iconClass) {
      html += `<div class="callout-icon"><i class="fas ${iconClass}"></i></div>`;
    }

    // Content wrapper - use standard callout structure
    html += '<div class="callout-content">';

    // Parse message - could be string or object with title/body
    if (typeof message === 'object' && message.title) {
      html += `<h4>${message.title}</h4>`;
      if (message.body) {
        html += `<p>${message.body}</p>`;
      }
    } else {
      html += `<p>${message}</p>`;
    }

    // Actions
    if (options.actions && options.actions.length > 0) {
      html += '<div class="notification-actions">';
      options.actions.forEach((action, index) => {
        const btnClass = action.class || 'button small';
        html += `<button class="${btnClass}" data-action="${index}">${action.text}</button>`;
      });
      html += '</div>';
    }

    html += '</div>';

    // Close button
    if (options.closable) {
      html += '<button class="callout-close" aria-label="Close notification"><i class="fas fa-times"></i></button>';
    }

    // Progress bar
    if (options.showProgress && options.duration > 0) {
      html += '<div class="notification-progress"></div>';
    }

    notification.innerHTML = html;

    // Ensure notifications are clickable while container is not
    notification.style.pointerEvents = 'auto';

    // Event listeners
    if (options.closable) {
      notification.querySelector('.callout-close').addEventListener('click', (e) => {
        e.stopPropagation();
        hideNotification(id);
      });
    }

    if (options.clickToDismiss) {
      notification.addEventListener('click', () => {
        hideNotification(id);
      });
    }

    // Action buttons
    if (options.actions && options.actions.length > 0) {
      notification.querySelectorAll('[data-action]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const actionIndex = parseInt(btn.getAttribute('data-action'));
          const action = options.actions[actionIndex];
          if (action.onClick) {
            action.onClick();
          }
          if (action.dismiss !== false) {
            hideNotification(id);
          }
        });
      });
    }

    return { id, element: notification };
  }

  /**
   * Show notification
   */
  function showNotification (notification, options) {
    const container = options.position === 'inline'
      ? options.container
      : getContainer(options.position);

    if (!container) {
      console.error('Notification container not found');
      return;
    }

    // Add to container
    if (options.position === 'inline') {
      // For inline, replace existing content
      container.innerHTML = '';
      container.appendChild(notification.element);
    } else {
      // For floating, prepend to stack
      container.insertBefore(notification.element, container.firstChild);
    }

    // Store reference
    activeNotifications.set(notification.id, {
      element: notification.element,
      options: options,
      timer: null,
    });

    // Trigger reflow for animation
    notification.element.offsetHeight;

    // Remove enter animation class
    setTimeout(() => {
      notification.element.classList.remove('notification-enter');
    }, 300);

    // Start auto-hide timer
    if (options.duration > 0) {
      const progressBar = notification.element.querySelector('.notification-progress');
      if (progressBar) {
        progressBar.style.transition = `width ${options.duration}ms linear`;
        setTimeout(() => {
          progressBar.style.width = '0';
        }, 10);
      }

      const timer = setTimeout(() => {
        hideNotification(notification.id);
      }, options.duration);

      activeNotifications.get(notification.id).timer = timer;
    }

    // Callback
    if (options.onShow) {
      options.onShow(notification.element);
    }

    return notification.id;
  }

  /**
   * Hide notification
   */
  function hideNotification (id) {
    const notification = activeNotifications.get(id);
    if (!notification) return;

    // Clear timer
    if (notification.timer) {
      clearTimeout(notification.timer);
    }

    // Immediately disable pointer events to prevent double-clicks
    notification.element.style.pointerEvents = 'none';
    
    // Add exit animation
    notification.element.classList.add('notification-exit');

    // Remove after animation (reduced from 300ms to 150ms for snappier response)
    setTimeout(() => {
      if (notification.element.parentNode) {
        notification.element.parentNode.removeChild(notification.element);
      }
      activeNotifications.delete(id);

      // Callback
      if (notification.options.onHide) {
        notification.options.onHide();
      }
    }, 150);
  }

  /**
   * Main notification function
   */
  function notify (message, options = {}) {
    // Merge options with defaults
    const opts = Object.assign({}, DEFAULTS, options);

    // Auto-detect icon if not provided
    if (!opts.icon && DEFAULT_ICONS[opts.type]) {
      opts.icon = DEFAULT_ICONS[opts.type];
    }

    // Create notification
    const notification = createNotificationElement(message, opts);

    // Show notification
    return showNotification(notification, opts);
  }

  /**
   * Convenience methods
   */
  const notificationMethods = {
    info: (message, options = {}) => notify(message, { ...options, type: 'info' }),
    success: (message, options = {}) => notify(message, { ...options, type: 'success' }),
    warning: (message, options = {}) => notify(message, { ...options, type: 'warning' }),
    error: (message, options = {}) => notify(message, { ...options, type: 'error' }),
    primary: (message, options = {}) => notify(message, { ...options, type: 'primary' }),
    secondary: (message, options = {}) => notify(message, { ...options, type: 'secondary' }),
  };

  /**
   * Clear all notifications
   */
  function clearAll () {
    activeNotifications.forEach((notification, id) => {
      hideNotification(id);
    });
  }

  /**
   * Clear notifications by type
   */
  function clearByType (type) {
    activeNotifications.forEach((notification, id) => {
      if (notification.options.type === type) {
        hideNotification(id);
      }
    });
  }

  /**
   * Update notification
   */
  function update (id, message, options = {}) {
    const notification = activeNotifications.get(id);
    if (!notification) return;

    // Update content
    const content = notification.element.querySelector('.notification-content p');
    if (content) {
      content.textContent = message;
    }

    // Update type if provided
    if (options.type && options.type !== notification.options.type) {
      notification.element.classList.remove(notification.options.type);
      notification.element.classList.add(options.type);
      notification.options.type = options.type;
    }

    // Update icon - check for explicit icon first, then use type default
    const icon = notification.element.querySelector('.callout-icon i');
    if (icon) {
      if (options.icon) {
        // Use explicitly provided icon
        icon.className = `fas ${options.icon}`;
      } else if (options.type && DEFAULT_ICONS[options.type]) {
        // Use default icon for type
        icon.className = `fas ${DEFAULT_ICONS[options.type]}`;
      }
    }
  }

  /**
   * Clear all notification containers from DOM
   */
  function clearContainers () {
    // Remove all containers from DOM
    Object.values(containers).forEach(container => {
      if (container && container.parentNode) {
        // Remove event listeners if they exist
        if (container._updatePosition) {
          window.removeEventListener('scroll', container._updatePosition);
          window.removeEventListener('resize', container._updatePosition);
        }
        container.remove();
      }
    });

    // Clear the containers object
    for (const key in containers) {
      delete containers[key];
    }

    // Also remove any orphaned containers
    document.querySelectorAll('.notification-container').forEach(container => {
      container.remove();
    });
  }

  // Public API
  window.StudioDome.notifications = {
    notify,
    ...notificationMethods,
    hide: hideNotification,
    update,
    clearAll,
    clearByType,
    clearContainers,

    // Configuration
    setDefaults: (newDefaults) => {
      Object.assign(DEFAULTS, newDefaults);
    },

    // Get active notifications
    getActive: () => Array.from(activeNotifications.keys()),

    // Debug helper
    getContainers: () => containers,
  };

  // Shorthand with convenience methods attached
  window.StudioDome.notify = notify;
  // Add convenience methods to notify function
  Object.assign(window.StudioDome.notify, notificationMethods);

})(window);